import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router'

import { TertiaryButton } from '@enechain/ecloud-designsystem'
import { FaWrenchRegular } from '@enechain/ecloud-icons'

import {
  EMPTY_PANEL_ICON_SIZE,
  EmptyPanel,
} from '~/components/common/EmptyPanel'
import { PageLayout } from '~/components/common/PageLayout'
import {
  findGroupByPathname,
  getGroupLandingPath,
} from '~/components/layouts/MainLayout/menuGroups'
import { resolveWorkflowIdParam } from '~/models/workflowIdParam'
import { routePaths } from '~/routes/routePaths'

type RouteKey = keyof typeof routePaths

/**
 * 説明文 2 行目 = その画面が使えるようになるまでの代替手段。
 *
 * 1 行目 (提供時期) だけだと「では今どうするか」が残り、担当者は毎回この画面まで来て
 * 確かめることになる。代替手段が実在する画面にだけ 2 行目を足す。
 *
 * **裏の取れた画面だけ書く**。実装済みの遷移先を指していない代替手段は、書かないより悪い
 * (存在しない導線を探させる)。未記入の画面は 1 行目だけで成立するので、文言が固まった
 * ものから足していけばよい。
 */
const ALTERNATIVE_I18N_KEYS: Partial<Record<RouteKey, string>> = {
  submissionManagement: 'common:placeholder.alternative.submissionManagement',
  planSubmission: 'common:placeholder.alternative.planSubmission',
}

type Props = {
  /** `routePaths` のキー。画面名 (`name`) と代替手段の引き当てに使う */
  routeKey: RouteKey
}

/**
 * 未実装ルートに出す「開発中」の面。
 *
 * `routes/index.tsx` が `implementedPages` の補集合に対して自動で当てるので、
 * 個別ページの実装が入るまでの間ここが表示される。
 *
 * 見た目は共通の**空状態パネル** (`design/ui-feasibility/components/empty-panel.md`)。
 * 専用の「開発中」部品は作らない — 「今は中身が無い」という意味は空状態と同じで、
 * DS も `PageError` 1 つで賄っているため。値は DS `PageError` に合わせる
 * (icon 80px / `Object.disabled`、title 20px/600、desc 14px/400・`pre-line`、
 * desc→ボタン 32px、ボタン = Tertiary)。
 *
 * `PageError` 自体は使えない。`100vw`/`100vh` の器に `70vh` で中央寄せする作りなので
 * サイドメニューとパンくずの内側に置けず、`kind` も 401/403/404/500/501 固定で
 * 「開発中」に当たる種別が無い (501 は「メンテナンス中」)。
 */
export const PlaceholderPage = ({ routeKey }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const pageName = t(routePaths[routeKey].name)

  const group = findGroupByPathname(location.pathname)
  /* 1 リンクに畳んだグループ (システム管理) は、 見出しがそのままトップ画面への入口なので
     パンくずの先頭もトップへのリンクにする (2026-10-05 デザイナー指示)。 サイドメニューに
     個別画面が無いため、 パンくずが無いと下層画面からトップへ戻る手段がメニューの 1 クリックしか無い。
     実装済みの管理画面も各ページで同じリンクを持つ (`routePaths.adminHub`)。 */
  const groupCrumb =
    group === undefined
      ? []
      : [
          {
            label: t(group.labelKey),
            ...(group.singleLink === true
              ? { to: getGroupLandingPath(group) }
              : {}),
          },
        ]
  const breadcrumbItems = [...groupCrumb, { label: pageName, bold: true }]

  // ワークフローのサブステップからの遷移は必ず `?workflowId=` 付き (`~/models/linkedPage.ts`)。
  // 付いていれば元のケース詳細へ戻し (= WorkflowBackBar と同じ戻り先)、
  // 直リンク・ブックマークで来た時は業務ワークフロー一覧 (= `/` の着地先) へ送る。
  // ここで行き止まりにすると、ワークフロー作業の途中で踏んだ人が復帰できない。
  const caseId = resolveWorkflowIdParam(searchParams)
  const backTo =
    caseId !== undefined
      ? routePaths.workflowCaseDetail.path.replace(':caseId', caseId)
      : routePaths.workflowCases.path
  const backLabel =
    caseId !== undefined
      ? t('common:placeholder.backToWorkflowCase')
      : t('common:placeholder.backToWorkflowCases')

  const alternativeKey = ALTERNATIVE_I18N_KEYS[routeKey]
  const description = [
    t('common:placeholder.description', { page: pageName }),
    ...(alternativeKey !== undefined ? [t(alternativeKey)] : []),
  ].join('\n')

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      {/* 見た目は共通の [[EmptyPanel]] に集約した (値は DS `PageError` が正)。
          ここで書き写すと「開発中」の面だけ他の空状態とずれる。 */}
      <EmptyPanel
        /* レンチ = 開発中 (時計は「受付中」に既出)。色は EmptyPanel が付ける。 */
        action={
          /* 遷移なので button ではなく link で描く
             (中クリック・新規タブを潰さない)。 */
          <TertiaryButton asChild>
            <RouterLink to={backTo}>{backLabel}</RouterLink>
          </TertiaryButton>
        }
        description={description}
        icon={
          <FaWrenchRegular boxSize={EMPTY_PANEL_ICON_SIZE} aria-hidden="true" />
        }
        title={t('common:placeholder.title')}
      />
    </PageLayout>
  )
}
